import { useState, useEffect } from 'react';
import type { UserManagement, CreateUserRequest, UpdateUserRequest } from '@/pages/users/api';
import { VALID_ROLES, VALID_APP_ACCESS } from '@/pages/users/constants';
import { CONTENT_TRACKER } from '@/hooks/useAppAccess';
import { FlagIcon } from '@/components/FlagIcon';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => void;
  user?: UserManagement | null;
  isLoading?: boolean;
  markets?: string[];
}

export const UserFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  isLoading = false,
  markets = [],
}: UserFormModalProps) => {
  const isEditing = !!user;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('planner');
  const [department, setDepartment] = useState('');
  const [functionalRole, setFunctionalRole] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedAppAccess, setSelectedAppAccess] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setEmail(user.email);
        setName(user.name);
        setRole(user.role);
        setDepartment(user.department || '');
        setFunctionalRole(user.functional_role || '');
        setSelectedMarkets(user.market || []);
        setSelectedChannels(user.channel || []);
        setSelectedAppAccess(user.app_access || []);
        setIsActive(user.is_active);
      } else {
        setEmail('');
        setName('');
        setRole('planner');
        setDepartment('');
        setFunctionalRole('');
        setSelectedMarkets([]);
        setSelectedChannels([]);
        // New users get Content Tracker access by default (matches the
        // backfill applied to existing users).
        setSelectedAppAccess([CONTENT_TRACKER]);
        setIsActive(true);
      }
      setErrors({});
    }
  }, [isOpen, user]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditing && !email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isEditing && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    if (isEditing) {
      const updateData: UpdateUserRequest = {
        name: name.trim(),
        role,
        department: department.trim() || undefined,
        functional_role: functionalRole.trim() || undefined,
        market: selectedMarkets,
        channel: selectedChannels,
        app_access: selectedAppAccess,
        is_active: isActive,
      };
      onSubmit(updateData);
    } else {
      const createData: CreateUserRequest = {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        department: department.trim() || undefined,
        functional_role: functionalRole.trim() || undefined,
        market: selectedMarkets,
        channel: selectedChannels,
        app_access: selectedAppAccess,
      };
      onSubmit(createData);
    }
  };

  const toggleMarket = (market: string) => {
    setSelectedMarkets((prev) =>
      prev.includes(market) ? prev.filter((m) => m !== market) : [...prev, market],
    );
  };

  const toggleChannel = (channel: string) => {
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel],
    );
  };

  const toggleAppAccess = (app: string) => {
    setSelectedAppAccess((prev) =>
      prev.includes(app) ? prev.filter((a) => a !== app) : [...prev, app],
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-xl border border-slate-200 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {isEditing ? 'Edit User' : 'Add New User'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {isEditing ? 'Update user details below.' : 'Fill in the details to create a new user.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEditing}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] outline-none ${
                isEditing
                  ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                  : 'bg-white text-slate-900'
              } ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
              placeholder="user@vml.com"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            {isEditing && (
              <p className="text-amber-600 text-xs mt-1.5 leading-relaxed">
                Email is the primary identifier and cannot be changed after creation. To change a
                user's email, deactivate this account and create a new user with the updated email
                address.
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white text-slate-900 focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] outline-none ${
                errors.name ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="John Doe"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm bg-white text-slate-900 focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] outline-none ${
                errors.role ? 'border-red-500' : 'border-slate-300'
              }`}
            >
              {VALID_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
          </div>

          {/* Department */}
          <div>
            <label htmlFor="department" className="block text-sm font-medium text-slate-700 mb-1">
              Department
            </label>
            <input
              type="text"
              id="department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] outline-none"
              placeholder="e.g., Marketing, Creative"
            />
          </div>

          {/* Functional Role */}
          <div>
            <label
              htmlFor="functionalRole"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Functional Role
            </label>
            <input
              type="text"
              id="functionalRole"
              value={functionalRole}
              onChange={(e) => setFunctionalRole(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] outline-none"
              placeholder="e.g., Activation Lead, Asset Localization"
            />
          </div>

          {/* Markets */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Markets</label>
            <div className="flex flex-wrap gap-2">
              {markets.map((market) => (
                <button
                  key={market}
                  type="button"
                  onClick={() => toggleMarket(market)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
                    selectedMarkets.includes(market)
                      ? 'bg-[#0014cc]/10 border-[#0014cc]/30 text-[#0014cc]'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <FlagIcon countryCode={market} className="w-4 h-auto rounded-sm" />
                  <span>{market}</span>
                </button>
              ))}
            </div>
            {selectedMarkets.length > 0 && (
              <p className="text-slate-500 text-xs mt-2">Selected: {selectedMarkets.join(', ')}</p>
            )}
          </div>

          {/* Channels */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Channels</label>
            <div className="flex flex-wrap gap-2">
              {['Paid Search', 'Paid Social'].map((channel) => (
                <button
                  key={channel}
                  type="button"
                  onClick={() => toggleChannel(channel)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
                    selectedChannels.includes(channel)
                      ? 'bg-[#0014cc]/10 border-[#0014cc]/30 text-[#0014cc]'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>{channel}</span>
                </button>
              ))}
            </div>
            {selectedChannels.length > 0 && (
              <p className="text-slate-500 text-xs mt-2">Selected: {selectedChannels.join(', ')}</p>
            )}
          </div>

          {/* App Access */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">App Access</label>
            <div className="flex flex-wrap gap-2">
              {VALID_APP_ACCESS.map((app) => (
                <button
                  key={app.value}
                  type="button"
                  onClick={() => toggleAppAccess(app.value)}
                  aria-pressed={selectedAppAccess.includes(app.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
                    selectedAppAccess.includes(app.value)
                      ? 'bg-[#0014cc]/10 border-[#0014cc]/30 text-[#0014cc]'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>{app.label}</span>
                </button>
              ))}
            </div>
            {selectedAppAccess.length === 0 && (
              <p className="text-slate-500 text-xs mt-2">
                No apps selected — this user has no app access recorded.
              </p>
            )}
          </div>

          {/* Is Active (only for editing) */}
          {isEditing && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-[#0014cc] border-slate-300 rounded accent-[#0014cc] focus:ring-[#0014cc]/30"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-slate-700">
                User is active
              </label>
            </div>
          )}
        </form>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#0014cc] rounded-lg hover:bg-[#0010a3] transition-colors disabled:bg-[#0014cc]/50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {isEditing ? 'Saving...' : 'Creating...'}
              </span>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Create User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
